const TOKEN_KEY = "auth_token"

type Credentials = {
  identifier: { type: "email" | "phone"; value: string }
  password: string
}

type SignUpCredentials = {
  identifier: {
    type: "email" | "phone"
    value: string
  }
  verification_code?: string
  password: string
  password_confirmation: string
}

// A phone account has no email address: it signs in with its number.
type AuthUser = {
  id: number
  email: string | null
  phone_number: string | null
}

type AuthResponse = {
  token: string
  user: AuthUser
}

const PHONE_PATTERN = /^1[3-9]\d{9}$/

export function isPhoneNumber(value: string): boolean {
  return PHONE_PATTERN.test(value)
}

export function identifierFor(value: string): Credentials["identifier"] {
  return { type: isPhoneNumber(value) ? "phone" : "email", value }
}

/**
 * Signup no longer always hands back a token: a new account has to confirm its
 * email address first, and until then there is nothing to sign in to.
 */
export type SignUpResult =
  | { status: "signed_in"; token: string; user: AuthUser }
  | { status: "pending_confirmation"; email: string; message: string }

type AuthError = {
  errors?: string[]
  error?: string
  status?: string
  purge_at?: string
}

/**
 * Carries the server's `status` so callers can react to `unconfirmed`, and the
 * `purge_at` that comes with `pending_deletion` — the deadline is the whole
 * point of that screen, so it travels with the error rather than being fetched
 * separately.
 */
export class AuthRequestError extends Error {
  readonly code?: string
  readonly purgeAt?: string

  constructor(message: string, code?: string, purgeAt?: string) {
    super(message)
    this.name = "AuthRequestError"
    this.code = code
    this.purgeAt = purgeAt
  }
}

export function storeAuthToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY)
}

async function authErrorFrom(res: Response): Promise<AuthRequestError> {
  try {
    const body = (await res.json()) as AuthError
    if (body.errors?.length) {
      return new AuthRequestError(
        body.errors.join("，"),
        body.status,
        body.purge_at,
      )
    }
    if (body.error)
      return new AuthRequestError(body.error, body.status, body.purge_at)
  } catch {
    // ponytail: non-JSON error bodies fall through to generic message
  }

  return new AuthRequestError("请求失败，请稍后再试")
}

const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
}

export async function signIn(credentials: Credentials): Promise<AuthResponse> {
  const res = await fetch("/api/v1/login", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ user: credentials }),
  })

  if (!res.ok) throw await authErrorFrom(res)

  return res.json() as Promise<AuthResponse>
}

export async function signUp(
  credentials: SignUpCredentials,
): Promise<SignUpResult> {
  const res = await fetch("/api/v1/signup", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ user: credentials }),
  })

  if (!res.ok) throw await authErrorFrom(res)

  return res.json() as Promise<SignUpResult>
}

export async function requestPhoneCode(
  phone_number: string,
  purpose: "signup" | "password_reset" = "signup",
): Promise<{ message: string; debug_code?: string }> {
  const path =
    purpose === "signup"
      ? "/api/v1/verification-code"
      : "/api/v1/password/verification-code"
  const res = await fetch(path, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ user: { phone_number } }),
  })

  if (!res.ok) throw await authErrorFrom(res)
  return res.json() as Promise<{ message: string; debug_code?: string }>
}

export async function requestPasswordReset(
  credentials:
    | { identifier: { type: "email"; value: string } }
    | {
        identifier: { type: "phone"; value: string }
        verification_code: string
      },
): Promise<{
  message?: string
  reset_password_token?: string
  reset_url?: string
}> {
  const res = await fetch("/api/v1/password", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ user: credentials }),
  })

  if (!res.ok) throw await authErrorFrom(res)
  return res.json() as Promise<{
    message?: string
    reset_password_token?: string
    reset_url?: string
  }>
}

/**
 * Restore a pending-deletion account with its identifier and password.
 */
export async function restoreAccount(
  credentials: Credentials,
): Promise<AuthResponse> {
  const res = await fetch("/api/v1/account/restore", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ user: credentials }),
  })

  if (!res.ok) throw await authErrorFrom(res)

  return res.json() as Promise<AuthResponse>
}

export async function resendConfirmation(email: string): Promise<string> {
  const res = await fetch("/api/v1/confirmation", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ user: { email } }),
  })

  if (!res.ok) throw await authErrorFrom(res)

  const body = (await res.json()) as { message?: string }
  return body.message ?? "确认邮件已重新发送，请查收。"
}

export async function signOut(): Promise<void> {
  const token = getAuthToken()
  if (!token) return

  try {
    await fetch("/api/v1/logout", {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
  } finally {
    clearAuthToken()
  }
}

export function requireAuth(redirectTo = "/dashboard"): boolean {
  if (getAuthToken()) return true

  const target = `/login?redirect=${encodeURIComponent(redirectTo)}`
  window.location.assign(target)
  return false
}
